// src/new/components/monitor/TriageChat/expressProtocol.ts

/**
 * Small, UI-friendly events the express-code chat backend's SSE stream is
 * reduced to. `parseExpressChatEvent` is the only thing that needs to know
 * express-code's wire shape — everything downstream only ever sees this union.
 */
export type ExpressChatEvent =
  | { kind: 'ready'; resumePrompt: string | null }
  | {
      kind: 'text';
      text: string;
      replace: boolean;
      isSubagent: boolean;
      childSessionId: string | null;
      // OpenCode message + part identity. One turn can emit MULTIPLE assistant
      // messages (each a distinct `messageId`), and a message can carry multiple
      // parts (each a distinct `partId`). The consumer keys a bubble per
      // `messageId` and accumulates text per `partId`, so distinct messages
      // don't collapse onto one another and multi-part messages don't clobber.
      messageId: string | null;
      partId: string | null;
    }
  | {
      kind: 'subagent-start';
      childSessionId: string;
      agent: string | null;
      title: string | null;
      prompt: string | null;
    }
  | { kind: 'subagent-end'; childSessionId: string }
  | { kind: 'history-refresh' }
  | { kind: 'done' }
  | { kind: 'error'; detail: string }
  | { kind: 'ignore' };

/** `child_session_id` is validated the same way for both subagent-start and
 *  subagent-end frames — extracted so the two cases can't drift apart. */
function childSessionId(env: Record<string, any>): string | null {
  return typeof env.child_session_id === 'string' && env.child_session_id
    ? env.child_session_id
    : null;
}

/**
 * Parse one express-code SSE frame — the `event:` name plus the raw `data:`
 * JSON string, already split apart by the caller — into an `ExpressChatEvent`.
 */
export function parseExpressChatEvent(name: string, rawData: string): ExpressChatEvent {
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawData);
  } catch {
    return { kind: 'ignore' };
  }
  if (!parsed || typeof parsed !== 'object') return { kind: 'ignore' };
  // Narrowed to an object above; the wire JSON is dynamically shaped and each
  // field is probed defensively below, so treat it as an index map rather than
  // a bare top-level `any`.
  const env = parsed as Record<string, any>;

  switch (name) {
    case 'ready':
      // resumePrompt is set only on the /tail resume handshake, when the
      // client re-attaches to a turn that was already in flight.
      return { kind: 'ready', resumePrompt: env.resume_prompt ?? null };

    case 'message-delta': {
      const isSubagent = Boolean(env.is_subagent);
      // subagentSessionId is non-null only for a subagent turn.
      const subagentSessionId = isSubagent ? (env.session_id ?? null) : null;
      const props = env.data?.properties ?? {};
      if (env.upstream === 'message.part.delta') {
        // A delta references its part/message; shapes vary across opencode
        // versions, so probe the common locations.
        const messageId = props.part?.messageID ?? props.messageID ?? null;
        const partId = props.part?.id ?? props.partID ?? props.partId ?? null;
        return {
          kind: 'text',
          text: props.delta ?? '',
          replace: false,
          isSubagent,
          childSessionId: subagentSessionId,
          messageId,
          partId,
        };
      }
      if (env.upstream === 'message.part.updated' && props.part?.type === 'text') {
        // A part.updated carries the part's full cumulative text — snap to
        // it rather than appending, unlike a part.delta token.
        return {
          kind: 'text',
          text: props.part.text ?? '',
          replace: true,
          isSubagent,
          childSessionId: subagentSessionId,
          messageId: props.part.messageID ?? null,
          partId: props.part.id ?? null,
        };
      }
      return { kind: 'ignore' };
    }

    case 'subagent-start': {
      const id = childSessionId(env);
      if (!id) return { kind: 'ignore' };
      return {
        kind: 'subagent-start',
        childSessionId: id,
        agent: env.agent ?? null,
        title: env.title ?? null,
        prompt: env.prompt ?? null,
      };
    }

    case 'subagent-end': {
      const id = childSessionId(env);
      return id ? { kind: 'subagent-end', childSessionId: id } : { kind: 'ignore' };
    }

    case 'history-refresh':
      // Background reconciliation signal fired mid-stream when the
      // server-side snapshot has drifted from what's being rendered live —
      // the consumer should re-run getHistory without disturbing the
      // currently-streaming bubble.
      return { kind: 'history-refresh' };

    case 'done':
      return { kind: 'done' };

    case 'error':
      return { kind: 'error', detail: env.detail ?? 'Unknown error' };

    default:
      return { kind: 'ignore' };
  }
}

/**
 * Build the /chat request body. `mode_id` is passed on the first turn only
 * (belt-and-suspenders alongside session creation) — the session's first
 * turn locks the mode, and later turns omit it.
 */
export function buildChatRequest(prompt: string, isFirstTurn: boolean) {
  return {
    prompt,
    ...(isFirstTurn ? { mode_id: 'triage' } : {}),
  };
}
